import { useEffect, useMemo, useState, memo } from 'react'
import { OptionsBar } from './OptionsBar'
import { ChatTextInput, ChatVoiceInput, ChatPushToTalkInput } from 'ui'
import useTranscribe from 'components/hooks/transcribe'
import { T, useTranslate } from '@tolgee/react'
import { ContextMenu } from 'components/context-menu'
import { useGetTranscription } from 'toolComponents/generic/audio/transcription'
import Lottie from 'react-lottie'
import * as animationData from './sparkle-lottie.json'

const defaultOptions = {
  loop: true,
  autoplay: true,
  animationData: animationData,
  rendererSettings: {
    preserveAspectRatio: 'xMidYMid slice',
  },
}

export const ChatForm = memo(
  ({
    isGenerating,
    handleSubmit,
    state,
    dispatch,
    handleClearContext,
    documentContext,
    audioStream,
    setAudioStream,
    handleStopRequest,
    messageContext,
    isHistoryVisible,
    width,
    setTtsIndex,
    setShowWebCam,
    lastUserMessage,
  }) => {
    const { t } = useTranslate()
    const [inputMode, setInputMode] = useState('text')

    useEffect(() => {
      if (inputMode === 'text') {
        dispatch({ type: 'SET_AUTO_AUDIO', payload: false })
      } else {
        dispatch({ type: 'SET_AUTO_AUDIO', payload: true })
      }
    }, [inputMode, dispatch])

    const textInput = useMemo(
      () => (
        <ChatTextInput
          state={state}
          dispatch={dispatch}
          handleSubmit={handleSubmit}
          messageContext={messageContext}
          isGenerating={isGenerating}
          setShowWebCam={setShowWebCam}
          handleStopRequest={handleStopRequest}
          t={t}
          useTranscribe={useTranscribe}
          ContextMenu={ContextMenu}
          audioStream={audioStream}
          setAudioStream={setAudioStream}
        />
      ),
      [
        state,
        dispatch,
        handleSubmit,
        messageContext,
        t,
        isGenerating,
        setShowWebCam,
        handleStopRequest,
        audioStream,
        setAudioStream,
      ]
    )

    const pushToTalkInput = useMemo(
      () => (
        <ChatPushToTalkInput
          state={state}
          handleSubmit={handleSubmit}
          isGenerating={isGenerating}
          handleStopRequest={handleStopRequest}
          audioStream={audioStream}
          setAudioStream={setAudioStream}
          t={t}
          T={T}
          useGetTranscription={useGetTranscription}
          loader={<Lottie options={defaultOptions} height={24} width={24} />}
        />
      ),
      [state, isGenerating, handleSubmit, t, handleStopRequest, audioStream, setAudioStream]
    )

    const voiceInput = useMemo(
      () => (
        <ChatVoiceInput
          dispatch={dispatch}
          handleSubmit={handleSubmit}
          audioStream={audioStream}
          setAudioStream={setAudioStream}
          lastUserMessage={lastUserMessage}
          state={state}
          t={t}
          T={T}
          useTranscribe={useTranscribe}
        />
      ),
      [dispatch, handleSubmit, t, audioStream, setAudioStream, lastUserMessage, state]
    )

    const inputs = {
      text: textInput,
      read: textInput,
      voice: voiceInput,
      push: pushToTalkInput,
    }

    return (
      <>
        <div className='bg-brand-chat-background border-brand-form-accent w-full border-t'>
          <div className='relative flex w-full flex-col items-center gap-[10px] p-2 py-3 sm:p-4 md:px-[30px] md:py-[20px] 2xl:px-[72px]'>
            {/* Input window */}
            {inputs[inputMode]}
            {/* Options bar */}
            <OptionsBar
              state={state}
              dispatch={dispatch}
              documentContext={documentContext}
              handleClearContext={handleClearContext}
              width={width}
              isHistoryVisible={isHistoryVisible}
              inputMode={inputMode}
              setInputMode={setInputMode}
            />
          </div>
        </div>
      </>
    )
  }
)
